import { useCallback, useEffect, useMemo, useState } from "react";
import { Divider, useResizable } from "../components/Split";
import { TopBar, type Tab } from "../components/TopBar";
import {
  api,
  Unauthorized,
  type FactorMeta,
  type RankRow,
  type RecipeEvidence,
  type RecipeSummary,
} from "../lib/api";
import { Evidence } from "./Evidence";
import { ReliabilityStrip } from "./ReliabilityStrip";
import { Workspace } from "./Workspace";

type Mode = "factor" | "buylist";

/**
 * The shell: icon rail, resizable left (universe) and right (evidence) panes, and a
 * centre workspace. Modelled on the tessa Studio layout, with the rails made
 * resizable — an operator decides the split, not the designer.
 *
 * The date control sits at the top left on purpose. It is not a filter; it is the
 * product's whole thesis. Everything on screen is "as known on" that date, and
 * putting it anywhere less prominent would invite the reader to forget that.
 */
export function Terminal({
  user,
  onLogout,
  tab,
  onTab,
}: {
  user: string;
  onLogout: () => void;
  tab: Tab;
  onTab: (t: Tab) => void;
}) {
  // Open on the latest COMPLETE month-end, not a frozen date. The fallback (last day
  // of the previous month) is always a complete month-end, so the first render never
  // requests a future/incomplete one; the authoritative value from /recipes
  // (latest_month_end — the exact date the nightly warm cached) replaces it on load.
  const [on, setOn] = useState(latestMonthEndFallback);
  const [mode, setMode] = useState<Mode>("buylist");
  const [factor, setFactor] = useState("gross_profitability");
  const [recipe, setRecipe] = useState("momentum");
  const [sector, setSector] = useState("");
  const [excludeFinancials, setExcludeFinancials] = useState(false);
  const [rows, setRows] = useState<RankRow[]>([]);
  const [meta, setMeta] = useState<{ total: number; cached: boolean; ms: number } | null>(null);
  const [factors, setFactors] = useState<FactorMeta[]>([]);
  const [recipes, setRecipes] = useState<RecipeSummary[]>([]);
  const [sectors, setSectors] = useState<string[]>([]);
  // How current the PRICE INPUTS are — not the same question as the as-of date above.
  const [pricesThrough, setPricesThrough] = useState<string | null>(null);
  const [evidence, setEvidence] = useState<{ e: RecipeEvidence; description: string } | null>(null);
  const [selected, setSelected] = useState<RankRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [leftW, setLeftW] = useResizable("left", 420, 300, 720);
  const [rightW, setRightW] = useResizable("right", 340, 240, 620);

  useEffect(() => {
    api.factors().then((r) => setFactors(r.factors)).catch(() => {});
    api
      .recipes()
      .then((r) => {
        setRecipes(r.recipes);
        setRecipe(r.default);
        setSectors(r.sectors);
        // Authoritative latest month-end (what the warm actually cached). Set once on
        // load; the user's own date changes happen afterwards and are not overridden.
        if (r.latest_month_end) setOn(r.latest_month_end);
        setPricesThrough(r.prices_through);
      })
      .catch(() => {});
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (mode === "buylist") {
        const res = await api.buylist(on, recipe, {
          limit: 300,
          excludeFinancials,
          ...(sector ? { sector } : {}),
        });
        setRows(res.rows);
        setMeta({ total: res.n_investable, cached: false, ms: res.elapsed_ms });
        setEvidence({ e: res.evidence, description: res.description });
        setSelected(res.rows[0] ?? null);
      } else {
        const res = await api.rank(on, factor, { limit: 300, excludeFinancials });
        setRows(res.rows);
        setMeta({ total: res.total, cached: res.cached, ms: res.elapsed_ms });
        setEvidence(null);
        setSelected(res.rows[0] ?? null);
      }
    } catch (e) {
      if (e instanceof Unauthorized) onLogout();
      else setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [mode, on, factor, recipe, sector, excludeFinancials, onLogout]);

  useEffect(() => {
    void load();
  }, [load]);

  // The factor whose score/description the evidence rail should describe. In buy-list
  // mode that is the RECIPE's factor (momentum/volatility), NOT the Explore factor
  // dropdown — otherwise a momentum-ranked name showed "Operating income / revenue"
  // because the Explore state was last left on operating margin.
  const activeFactor =
    mode === "buylist" ? recipes.find((r) => r.name === recipe)?.factor : factor;
  const current = useMemo(
    () => factors.find((f) => f.name === activeFactor),
    [factors, activeFactor],
  );

  return (
    <div className="h-full flex flex-col">
      <TopBar user={user} onLogout={onLogout} tab={tab} onTab={onTab} />

      <div className="flex-1 min-h-0 flex">
        {/* LEFT — the universe */}
        <aside
          style={{ width: leftW }}
          className="shrink-0 flex flex-col border-r border-term-edge bg-term-panel min-h-0"
        >
          <div className="p-2 space-y-2 border-b border-term-edge">
            {/* Mode: a curated buy list, or free factor exploration. The buy list is
                the default — it is what the tool is FOR; the factor browser is the
                lab behind it. */}
            <div className="flex rounded border border-term-edge overflow-hidden text-[11px]">
              {(["buylist", "factor"] as Mode[]).map((m) => (
                <button
                  key={m}
                  onClick={() => setMode(m)}
                  className={`flex-1 px-2 py-1 ${
                    mode === m ? "bg-term-accent/20 text-term-accent" : "text-term-dim hover:text-term-text"
                  }`}
                >
                  {m === "buylist" ? "Buy list" : "Explore"}
                </button>
              ))}
            </div>

            <div>
              <label className="block text-[10px] uppercase tracking-wide text-term-dim mb-1">
                As known on
              </label>
              <input
                type="date"
                value={on}
                onChange={(e) => setOn(e.target.value)}
                className="w-full bg-term-bg border border-term-edge rounded px-2 py-1
                           outline-none focus:border-term-accent tnum"
              />
              <PriceCurrency through={pricesThrough} />
            </div>

            {mode === "buylist" ? (
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-term-dim mb-1">
                  Recipe
                </label>
                <select
                  value={recipe}
                  onChange={(e) => setRecipe(e.target.value)}
                  className="w-full bg-term-bg border border-term-edge rounded px-2 py-1
                             outline-none focus:border-term-accent"
                >
                  {recipes.map((r) => (
                    <option key={r.name} value={r.name}>
                      {r.name}
                      {r.survives_neutralization ? "  (stock selection)" : "  (allocation bet)"}
                    </option>
                  ))}
                </select>
                <label className="block text-[10px] uppercase tracking-wide text-term-dim mb-1 mt-2">
                  Sector
                </label>
                <select
                  value={sector}
                  onChange={(e) => setSector(e.target.value)}
                  className="w-full bg-term-bg border border-term-edge rounded px-2 py-1
                             outline-none focus:border-term-accent"
                >
                  <option value="">all sectors</option>
                  {sectors.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </select>
                {sector && (
                  <p className="text-term-dim text-[10px] mt-1 leading-relaxed">
                    Names within {sector}. The recipe's record was measured on the full
                    universe — it is not re-measured per sector.
                  </p>
                )}
              </div>
            ) : (
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-term-dim mb-1">
                  Factor
                </label>
                <select
                  value={factor}
                  onChange={(e) => setFactor(e.target.value)}
                  className="w-full bg-term-bg border border-term-edge rounded px-2 py-1
                             outline-none focus:border-term-accent"
                >
                  {factors.map((f) => (
                    <option key={f.name} value={f.name}>
                      {f.name}
                      {f.direction === -1 ? "  (lower is better)" : ""}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <label className="flex items-center gap-2 text-[11px] text-term-dim">
              <input
                type="checkbox"
                checked={excludeFinancials}
                onChange={(e) => setExcludeFinancials(e.target.checked)}
              />
              exclude financials
            </label>
          </div>

          {/* Strip + count + names share ONE scroll region, so the names are always
              reachable by scrolling past the reliability strip rather than being
              trapped in a sliver beneath it. */}
          <div className="flex-1 overflow-auto min-h-0">
            {mode === "buylist" && evidence && (
              <ReliabilityStrip e={evidence.e} description={evidence.description} />
            )}

            <div className="px-2 py-1 text-[10px] text-term-dim border-b border-term-edge flex gap-2 sticky top-0 bg-term-panel z-10">
              {loading ? (
                <span className="animate-pulse">ranking…</span>
              ) : meta ? (
                <>
                  <span className="tnum">
                    {meta.total.toLocaleString()} {mode === "buylist" ? "investable" : "names"}
                  </span>
                  <span className={meta.cached ? "text-term-up" : "text-term-dim"}>
                    {mode === "buylist" ? "" : meta.cached ? "cached " : "computed "}
                    {meta.ms.toFixed(0)}ms
                  </span>
                </>
              ) : null}
            </div>

            {error && <div className="p-2 text-term-down text-[11px]">{error}</div>}
            <table className="w-full">
              <tbody>
                {rows.map((r) => (
                  <tr
                    key={`${r.cik}-${r.rank}`}
                    onClick={() => setSelected(r)}
                    className={`cursor-pointer border-b border-term-edge/40 hover:bg-term-edge/40 ${
                      selected?.cik === r.cik ? "bg-term-accent/15" : ""
                    }`}
                  >
                    <td className="px-2 py-1 text-term-dim tnum w-10 text-right">{r.rank}</td>
                    <td className="px-1 py-1 font-medium">{r.ticker}</td>
                    <td className="px-1 py-1 text-term-dim truncate max-w-[140px]">{r.title}</td>
                    <td className="px-2 py-1 tnum text-right text-term-dim">
                      {r.zscore === null ? "—" : r.zscore.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </aside>

        <Divider side="left" onResize={(d) => setLeftW((w) => Math.min(Math.max(w + d, 300), 720))} />

        {/* CENTRE — the workspace */}
        <main className="flex-1 min-w-0 min-h-0">
          <Workspace ticker={selected?.ticker ?? null} />
        </main>

        <Divider
          side="right"
          onResize={(d) => setRightW((w) => Math.min(Math.max(w + d, 240), 620))}
        />

        {/* RIGHT — the evidence */}
        <aside
          style={{ width: rightW }}
          className="shrink-0 border-l border-term-edge bg-term-panel min-h-0 overflow-auto"
        >
          <Evidence row={selected} on={on} factor={current ?? null} />
        </aside>
      </div>
    </div>
  );
}

/**
 * How current the price inputs are, stated rather than implied.
 *
 * The as-of control above answers "which cross-section is this?". It does NOT answer
 * "how fresh is the data behind it?", and the two diverge permanently now that price
 * acquisition has ended (decisions.md L3). A buy list computed from a frozen feed is
 * still point-in-time-honest — the as-of date is explicit, nothing sees the future —
 * but a reader who is not told the feed stops will assume it does not, which is
 * exactly the quietly-true-but-misleading artifact this project exists to refuse.
 *
 * Silent while the feed is current (inside a week), so it reads as a real signal
 * rather than permanent furniture.
 */
function PriceCurrency({ through }: { through: string | null }) {
  if (!through) return null;
  const days = Math.floor((Date.now() - Date.parse(`${through}T00:00:00Z`)) / 86_400_000);
  if (days <= 7) return null;
  return (
    <p className="mt-1 text-[10px] leading-relaxed text-term-down">
      Prices frozen at <span className="tnum">{through}</span> ({days}d ago). The ranking is
      honest as of the date above; it is not built on current prices.
    </p>
  );
}

/** The last day of the PREVIOUS month (UTC) — always a complete month-end, so it is a
 *  safe initial `on` before /recipes returns the authoritative latest_month_end. */
function latestMonthEndFallback(): string {
  const now = new Date();
  const lastOfPrev = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 0));
  return lastOfPrev.toISOString().slice(0, 10);
}
